import React from 'react'
import { getBtnContentColor } from './aux'
import { ButtonProps } from './types'
import { Theme } from '@/constants/theme'
import {
  EmailInline,
  Phone,
  Apple,
  Google,
  Telegram,
  PlusCircle,
  Exchange,
  Copy,
  Upload,
  Pin,
  Unpin,
  GroupIcon,
} from '@/assets/icons'

interface ChildrenProps extends ButtonProps {
  theme: Theme
}

// Icon mapping by name
const IconMap = {
  emailInline: EmailInline,
  phone: Phone,
  apple: Apple,
  google: Google,
  telegram: Telegram,
  plusCircle: PlusCircle,
  exchange: Exchange,
  copy: Copy,
  upload: Upload,
  pin: Pin,
  unpin: Unpin,
  groupIcon: GroupIcon,
}

export const Children: React.FC<ChildrenProps> = ({
  isLoading,
  label,
  disabled,
  type = 'primaryGradient',
  theme,
  iconName,
  iconPosition = 'left',
  iconSize = 24,
  isDefaultIconColor = false,
  labelProps,
  fontFamily = 'bold', // Default value
}) => {
  // Get CSS class for fontFamily (similar to BaseText)
  const getFontWeightClass = (fontFamily: string): string => {
    switch (fontFamily) {
      case 'light':
        return 'font-light' // font-weight: 300
      case 'regular':
        return 'font-normal' // font-weight: 400
      case 'medium':
        return 'font-medium' // font-weight: 500
      case 'semibold':
        return 'font-semibold' // font-weight: 600
      case 'bold':
        return 'font-bold' // font-weight: 700
      default:
        return 'font-medium' // default
    }
  }

  // Get CSS fontWeight for inline styles (as fallback)
  const getFontWeight = (fontFamily: string): string | number => {
    switch (fontFamily) {
      case 'light':
        return 300
      case 'regular':
        return 400
      case 'medium':
        return 500
      case 'semibold':
        return 600
      case 'bold':
        return 700
      default:
        return 500
    }
  }

  const contentColor = getBtnContentColor(type, theme, !!disabled)

  // Get icon by name
  const IconComponent = iconName
    ? IconMap[iconName as keyof typeof IconMap]
    : null

  // Determine icon color
  const getIconColor = () => {
    if (isDefaultIconColor) {
      // For Google icon use original colors
      if (iconName === 'google') return undefined
      // For Apple icon use black color on light buttons
      if (iconName === 'apple' && type === 'secondaryGrayDark') return '#000'
      // For Telegram use brand color
      if (iconName === 'telegram') return '#0088cc'
    }
    return contentColor.color
  }

  return (
    <>
      {isLoading && (
        <div
          className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent"
          style={{
            borderColor: contentColor.color,
            borderTopColor: 'transparent',
          }}
        />
      )}

      {!isLoading && IconComponent && iconPosition === 'left' && (
        <IconComponent
          color={getIconColor()}
          size={iconSize}
          style={{ flexShrink: 0 }}
        />
      )}

      {!isLoading && label && (
        <span
          className={getFontWeightClass(labelProps?.fontFamily || fontFamily)}
          style={{
            color: contentColor.color,
            fontSize: 'inherit',
            fontWeight: getFontWeight(labelProps?.fontFamily || fontFamily), // fallback for inline styles
            ...labelProps?.style,
          }}
        >
          {label}
        </span>
      )}

      {!isLoading && IconComponent && iconPosition === 'right' && (
        <IconComponent
          color={getIconColor()}
          size={iconSize}
          style={{ flexShrink: 0 }}
        />
      )}
    </>
  )
}
